import { KEY_LABEL } from "components/CommandPalette/constants";
import { BULLET_LIST, ORDERED_LIST } from "constants/richText";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { Bold } from "components/icons/Bold";
import { Italic } from "components/icons/Italic";
import { LinkIcon } from "components/icons/LinkIcon";
import { ListIcon } from "components/icons/ListIcon";
import { NumberedList } from "components/icons/NumberedList";
import { useDeviceInfo } from "hooks/useDeviceInfo";
import { Fragment, MouseEvent, ReactNode } from "react";

type RichTextToolbarProps = {
  formats: Record<string, unknown>;
  isDisabled?: boolean;
  onBold: () => void;
  onItalic: () => void;
  onLink: () => void;
  onNumberedList: () => void;
  onBulletedList: () => void;
};

type ToolbarItem = {
  label: string;
  shortcut: string;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
};

type ToolbarToggleProps = ToolbarItem & {
  isDisabled: boolean;
};

const SHIFT_KEY_LABEL = "⇧";

const preventFocusLoss = (e: MouseEvent<HTMLButtonElement>) =>
  e.preventDefault();

const ToolbarToggle = ({
  label,
  shortcut,
  icon,
  isActive,
  onClick,
  isDisabled,
}: ToolbarToggleProps) => (
  <ToolTip text={`${label} (${shortcut})`}>
    <Button
      type="button"
      customType="ghost_icon"
      size="small"
      aria-label={label}
      aria-pressed={isActive}
      disabled={isDisabled}
      onMouseDown={preventFocusLoss}
      onClick={onClick}
      className="aria-pressed:t-bg-neutral-10 aria-pressed:t-text-text-100"
    >
      {icon}
    </Button>
  </ToolTip>
);

const ToolbarDivider = () => (
  <div role="separator" className="t-mx-1 t-h-5 t-w-px t-bg-neutral-10" />
);

export const RichTextToolbar = ({
  formats,
  isDisabled = false,
  onBold,
  onItalic,
  onLink,
  onNumberedList,
  onBulletedList,
}: RichTextToolbarProps) => {
  const { isMac } = useDeviceInfo();
  const modifierKey = isMac ? KEY_LABEL.MAC_MODIFIER : KEY_LABEL.MODIFIER;
  const modifierShiftKey = `${modifierKey}${SHIFT_KEY_LABEL}`;

  const toolbarGroups: ToolbarItem[][] = [
    [
      {
        label: "Bold",
        shortcut: `${modifierKey}B`,
        icon: <Bold />,
        isActive: Boolean(formats.bold),
        onClick: onBold,
      },
      {
        label: "Italic",
        shortcut: `${modifierKey}I`,
        icon: <Italic />,
        isActive: Boolean(formats.italic),
        onClick: onItalic,
      },
    ],
    [
      {
        label: "Link",
        shortcut: `${modifierShiftKey}U`,
        icon: <LinkIcon />,
        isActive: Boolean(formats.link),
        onClick: onLink,
      },
    ],
    [
      {
        label: "Numbered list",
        shortcut: `${modifierShiftKey}7`,
        icon: <NumberedList />,
        isActive: formats.list === ORDERED_LIST,
        onClick: onNumberedList,
      },
      {
        label: "Bulleted list",
        shortcut: `${modifierShiftKey}8`,
        icon: <ListIcon />,
        isActive: formats.list === BULLET_LIST,
        onClick: onBulletedList,
      },
    ],
  ];

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="t-flex t-items-center t-justify-start t-gap-0.5"
    >
      {toolbarGroups.map((group, index) => (
        <Fragment key={group[0].label}>
          {index > 0 && <ToolbarDivider />}
          {group.map((item) => (
            <ToolbarToggle key={item.label} {...item} isDisabled={isDisabled} />
          ))}
        </Fragment>
      ))}
    </div>
  );
};
